import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { warehouseAPI } from '../../lib/api';
import { Loader2, Printer, Download, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { formatInWarehouseTimezone } from '../../lib/timezone';
import { formatQty } from '../../lib/qtyUtils';

/**
 * PrintablePO - Generates a vendor-ready, printable Purchase Order
 * 
 * Layout:
 * - Header: Company logo, name, phone/email, billing address
 * - Vendor block: Vendor name (address if available)
 * - Ship To block: Warehouse name/code + shipping address
 * - PO Info: PO number, date, created by
 * - Line items table
 * - Totals + Notes
 */
export const PrintablePO = ({ po, open, onOpenChange, onEmailClick }) => {
  const [companyBranding, setCompanyBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef(null);

  useEffect(() => {
    const loadBranding = async () => {
      if (!open) return;
      try {
        const response = await warehouseAPI.getCompanyBranding();
        setCompanyBranding(response.data);
      } catch (error) {
        console.error('Failed to load company branding:', error);
      } finally {
        setLoading(false);
      }
    };
    loadBranding();
  }, [open]);

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>PO ${po.po_number}</title>
        <style>
          ${getPrintStyles()}
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const formatAddress = (line1, line2, city, state, zip) => {
    const parts = [];
    if (line1) parts.push(line1);
    if (line2) parts.push(line2);
    const cityStateZip = [city, state].filter(Boolean).join(', ');
    if (cityStateZip || zip) {
      parts.push([cityStateZip, zip].filter(Boolean).join(' '));
    }
    return parts;
  };

  const companyAddress = formatAddress(
    companyBranding?.billing_address_line1,
    companyBranding?.billing_address_line2,
    companyBranding?.billing_city,
    companyBranding?.billing_state,
    companyBranding?.billing_postal_code
  );

  const shipToAddress = formatAddress(
    po.shipping_address_line1,
      po.shipping_address_line2,
    po.shipping_city,
    po.shipping_state,
    po.shipping_postal_code
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Purchase Order {po.po_number}</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => onEmailClick?.()} disabled={loading}>
                <Mail className="w-4 h-4 mr-2" />
                Email
              </Button>
              <Button variant="outline" size="sm" onClick={handlePrint} disabled={loading}>
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div ref={printRef} className="printable-po bg-white p-8" data-testid="printable-po-content">
            {/* Header */}
            <div className="po-header flex justify-between items-start border-b pb-6 mb-6">
              <div className="company-info">
                {companyBranding?.company_logo_url && (
                  <img 
                    src={companyBranding.company_logo_url} 
                    alt="Company Logo" 
                    className="h-16 w-auto mb-3 object-contain"
                  />
                )}
                <h1 className="text-2xl font-bold">{companyBranding?.company_name || 'Company Name'}</h1>
                {companyBranding?.company_phone && (
                  <p className="text-sm text-gray-600">{companyBranding.company_phone}</p>
                )}
                {companyBranding?.company_email && (
                  <p className="text-sm text-gray-600">{companyBranding.company_email}</p>
                )}
                {companyAddress.length > 0 && (
                  <div className="mt-2 text-sm text-gray-600">
                    {companyAddress.map((line, i) => (
                      <p key={i}>{line}</p>
                    ))}
                  </div>
                )}
              </div>
              <div className="po-title text-right">
                <h2 className="text-3xl font-bold text-gray-800">PURCHASE ORDER</h2>
                <p className="text-xl font-mono font-bold mt-2">{po.po_number}</p>
                <p className="text-sm text-gray-600 mt-1">
                  Date: {formatInWarehouseTimezone(po.submitted_at, { timezone: po.warehouse_timezone }, {
                    includeTime: false,
                    showTimezone: false,
                    dateStyle: 'medium'
                  })}
                </p>
              </div>
            </div>

            {/* Vendor & Ship To */}
            <div className="grid grid-cols-2 gap-8 mb-6">
              <div className="vendor-block">
                <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">Vendor</h3>
                <div className="border rounded p-4 bg-gray-50">
                  <p className="font-semibold text-lg">{po.vendor_name}</p>
                  {/* Vendor address would go here if available */}
                </div>
              </div>
              <div className="ship-to-block">
                <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">Ship To</h3>
                <div className="border rounded p-4 bg-gray-50">
                  <p className="font-semibold text-lg">
                    {po.warehouse_name || 'Warehouse'}
                    {po.warehouse_code && <span className="text-gray-500 ml-2">({po.warehouse_code})</span>}
                  </p>
                  {shipToAddress.length > 0 ? (
                    <div className="text-sm text-gray-600 mt-1">
                      {shipToAddress.map((line, i) => (
                        <p key={i}>{line}</p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic mt-1">No address on file</p>
                  )}
                </div>
              </div>
            </div>

            {/* PO Details */}
            <div className="po-details grid grid-cols-5 gap-4 mb-6 text-sm">
              <div className="border rounded p-3">
                <p className="text-xs text-gray-500 uppercase">PO Number</p>
                <p className="font-mono font-bold">{po.po_number}</p>
              </div>
              <div className="border rounded p-3">
                <p className="text-xs text-gray-500 uppercase">Date</p>
                <p className="font-medium">{formatInWarehouseTimezone(po.submitted_at, { timezone: po.warehouse_timezone }, {
                  includeTime: false,
                  showTimezone: false,
                  dateStyle: 'medium'
                })}</p>
              </div>
              <div className="border rounded p-3">
                <p className="text-xs text-gray-500 uppercase">Created By</p>
                <p className="font-medium">{po.created_by_name}</p>
              </div>
              <div className="border rounded p-3">
                <p className="text-xs text-gray-500 uppercase">Job</p>
                <p className="font-medium truncate">{po.job_name}</p>
                {po.job_number && <p className="text-xs text-gray-500">#{po.job_number}</p>}
              </div>
              <div className="border rounded p-3">
                <p className="text-xs text-gray-500 uppercase">Payment Terms</p>
                <p className="font-medium" data-testid="po-print-payment-terms">{po.vendor_payment_terms || '—'}</p>
              </div>
            </div>

            {/* Line Items Table */}
            <div className="line-items mb-6">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-800 text-white">
                    <th className="text-left p-3 text-sm font-medium">#</th>
                    <th className="text-left p-3 text-sm font-medium">Description</th>
                    <th className="text-center p-3 text-sm font-medium">Qty</th>
                    <th className="text-right p-3 text-sm font-medium">Unit Cost</th>
                    <th className="text-right p-3 text-sm font-medium">Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {po.line_items.map((item, index) => (
                    <tr key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="p-3 text-sm border-b">{index + 1}</td>
                      <td className="p-3 text-sm border-b">{item.item_name}</td>
                      <td className="p-3 text-sm text-center border-b">{formatQty(item.quantity)}</td>
                      <td className="p-3 text-sm text-right border-b">
                        {item.unit_cost != null ? `$${item.unit_cost.toFixed(2)}` : '—'}
                      </td>
                      <td className="p-3 text-sm text-right border-b font-medium">
                        {item.line_total != null ? `$${item.line_total.toFixed(2)}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="totals flex justify-end mb-6">
              <div className="w-64">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-sm text-gray-600">Subtotal:</span>
                  <span className="font-medium">
                    {po.total != null ? `$${po.total.toFixed(2)}` : '—'}
                  </span>
                </div>
                <div className="flex justify-between py-3 text-lg font-bold">
                  <span>Total:</span>
                  <span>{po.total != null ? `$${po.total.toFixed(2)}` : '—'}</span>
                </div>
              </div>
            </div>

            {/* Notes */}
            {(po.order_description || po.office_notes) && (
              <div className="notes border-t pt-4">
                <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">Notes</h3>
                {po.order_description && (
                  <p className="text-sm text-gray-700 mb-2">{po.order_description}</p>
                )}
                {po.office_notes && (
                  <p className="text-sm text-gray-600 italic">{po.office_notes}</p>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="po-footer border-t mt-8 pt-4 text-center text-xs text-gray-400">
              <p>Generated on {format(new Date(), 'MMM d, yyyy h:mm a')}</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Print-friendly CSS styles
const getPrintStyles = () => `
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 12pt;
    color: #1a1a1a;
    background: white;
  }
  
  .printable-po {
    padding: 40px;
    max-width: 800px;
    margin: 0 auto;
  }
  
  .po-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    border-bottom: 2px solid #e5e5e5;
    padding-bottom: 20px;
    margin-bottom: 20px;
  }
  
  .company-info h1 {
    font-size: 24pt;
    font-weight: bold;
    margin-bottom: 4px;
  }
  
  .company-info p {
    font-size: 10pt;
    color: #666;
    margin: 2px 0;
  }
  
  .company-info img {
    max-height: 60px;
    width: auto;
    margin-bottom: 8px;
  }
  
  .po-title {
    text-align: right;
  }
  
  .po-title h2 {
    font-size: 24pt;
    font-weight: bold;
    color: #333;
  }
  
  .po-title p {
    margin-top: 4px;
  }
  
  .grid {
    display: grid;
  }
  
  .grid-cols-2 {
    grid-template-columns: 1fr 1fr;
  }
  
  .grid-cols-4 {
    grid-template-columns: repeat(4, 1fr);
  }
  
  .gap-4 {
    gap: 16px;
  }
  
  .gap-8 {
    gap: 32px;
  }
  
  .mb-6 {
    margin-bottom: 24px;
  }
  
  .vendor-block h3,
  .ship-to-block h3 {
    font-size: 9pt;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #888;
    margin-bottom: 8px;
  }
  
  .vendor-block > div,
  .ship-to-block > div {
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 16px;
    background: #fafafa;
  }
  
  .po-details > div {
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 12px;
  }
  
  .po-details p:first-child {
    font-size: 8pt;
    text-transform: uppercase;
    color: #888;
    margin-bottom: 4px;
  }
  
  table {
    width: 100%;
    border-collapse: collapse;
  }
  
  thead tr {
    background: #1a1a1a;
    color: white;
  }
  
  th {
    padding: 12px;
    text-align: left;
    font-size: 10pt;
    font-weight: 500;
  }
  
  th:nth-child(3),
  td:nth-child(3) {
    text-align: center;
  }
  
  th:nth-child(4),
  th:nth-child(5),
  td:nth-child(4),
  td:nth-child(5) {
    text-align: right;
  }
  
  td {
    padding: 12px;
    border-bottom: 1px solid #eee;
    font-size: 10pt;
  }
  
  tbody tr:nth-child(even) {
    background: #f9f9f9;
  }
  
  .totals {
    display: flex;
    justify-content: flex-end;
  }
  
  .totals > div {
    width: 200px;
  }
  
  .totals .flex {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
  }
  
  .notes {
    border-top: 1px solid #eee;
    padding-top: 16px;
  }
  
  .notes h3 {
    font-size: 9pt;
    font-weight: bold;
    text-transform: uppercase;
    color: #888;
    margin-bottom: 8px;
  }
  
  .po-footer {
    border-top: 1px solid #eee;
    margin-top: 32px;
    padding-top: 16px;
    text-align: center;
    font-size: 9pt;
    color: #aaa;
  }
  
  @media print {
    body {
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
    }
    
    .printable-po {
      padding: 0;
    }
    
    thead tr {
      background: #1a1a1a !important;
      color: white !important;
    }
  }
`;

export default PrintablePO;
